import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import type {
  FieldDefinition,
  Id,
  Task,
  TaskLinkTarget,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { userLabel, useUserDirectory } from "@/modules/tasks/directories";
import { TASK_STATUS_OPTIONS } from "@/modules/tasks/task-enums";
import { TaskForm } from "@/modules/tasks/screens/TaskForm";

const STATUS_FIELD: FieldDefinition = {
  name: "status",
  label: "Status",
  type: "enum",
  options: TASK_STATUS_OPTIONS,
};

const DUE_DATE_FIELD: FieldDefinition = {
  name: "dueDate",
  label: "Due date",
  type: "date",
};

/**
 * One task row: reference, title, status, assignee and due date (FR-TSK-10 /
 * FR-TSK-11's acceptance shape), opening onto the full Tasks list rather than
 * a route this module does not own — `contacts` and `orders` have no
 * `/tasks/:id`, only Tasks itself does (`config/modules.ts`), and `all`
 * rather than `mine` because a task linked from an order was not necessarily
 * assigned to or watched by whoever is looking at the order right now.
 */
function TaskRow({
  task,
  users,
}: {
  task: Task;
  users: ReturnType<typeof useUserDirectory>["data"];
}) {
  const navigate = useNavigate();

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          void navigate({ to: "/tasks/all", search: { record: task.id } });
        }}
        className="hover:bg-muted/50 focus-visible:ring-ring flex w-full flex-col gap-1 px-1 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
      >
        {/*
          Two lines, not one wrapping flex row: reference, status, assignee
          and due date are four fixed-width labels that do not leave enough
          room for the title on a 390px drawer, and a `flex-1` item sharing a
          wrapped flex line can collapse to zero width instead of moving to
          its own line — which is what silently dropped the title here before
          this was two rows.
        */}
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {task.reference}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {task.title}
          </span>
        </span>
        <span className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {renderCell(STATUS_FIELD, task.status)}
          <span>
            {task.assigneeUserId
              ? userLabel(users?.items, task.assigneeUserId)
              : "Unassigned"}
          </span>
          <span>{renderCell(DUE_DATE_FIELD, task.dueDate)}</span>
        </span>
      </button>
    </li>
  );
}

/**
 * The Tasks section on an order or contact detail screen (FR-TSK-10,
 * FR-TSK-11, FR-TSK-17, #404): the tasks linked to that record, and a way to
 * file a new one already linked to it.
 *
 * Self-contained on purpose (#164 is editing `OrderDetail.tsx` for
 * write-through at the same time this ticket touches it) — every screen that
 * wants a Tasks card renders this one component, so the two changes do not
 * collide on the same lines.
 *
 * Hidden without `tasks.task.view` the way every other permission-gated
 * section is (a courtesy: `GET /api/tasks/targets/:type/:id` calls
 * `requirePermission()` regardless of what this renders).
 */
export function LinkedTasks({
  targetType,
  targetId,
  followUpTitle,
}: {
  targetType: TaskLinkTarget;
  targetId: Id;
  /**
   * Makes the card's create button "Create follow-up task" and opens the form
   * with this title already in it — "Follow up order #1187" (#445). Still the
   * one task form, with the same link and the same editable title; the person
   * picks the project, assignee and due date on the spot. A record that is not
   * followed up in this sense, a contact, leaves it out and keeps "New task".
   */
  followUpTitle?: string;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canView = can(permissions, "tasks.task.view");
  const [creating, setCreating] = useState(false);

  const usersQuery = useUserDirectory();
  const tasksQuery = useQuery({
    queryKey: ["tasks", "linked", targetType, targetId],
    queryFn: () => data.tasks.listForTarget(targetType, targetId),
    enabled: canView,
  });

  if (!canView) return null;

  return (
    <Card className="gap-3">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Tasks</CardTitle>
        {/*
          Hidden without `tasks.task.create` — a courtesy, not the
          enforcement: `POST /api/tasks` calls `requirePermission()`
          regardless, and a rejection from it still renders through
          `TaskForm`'s own rejection text.
        */}
        {can(permissions, "tasks.task.create") ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus />
            {followUpTitle ? "Create follow-up task" : "New task"}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {tasksQuery.isPending ? (
          <TableSkeleton rows={2} />
        ) : tasksQuery.isError ? (
          <LoadFailed
            message={tasksQuery.error.message}
            onRetry={() => {
              void tasksQuery.refetch();
            }}
          />
        ) : tasksQuery.data.items.length === 0 ? (
          <p className="text-muted-foreground text-sm">No tasks yet</p>
        ) : (
          <ul className="divide-y">
            {tasksQuery.data.items.map((task) => (
              <TaskRow key={task.id} task={task} users={usersQuery.data} />
            ))}
          </ul>
        )}
      </CardContent>

      {/*
        The project stays a real choice — FR-TSK-17 only pre-sets the link,
        never the project — so this is the same `TaskForm` every other "New
        task" opens, with `link` as the one extra prop. `TaskForm` reads it
        straight into `TasksRepository.create`'s own `link` option; nothing
        about *this* task being about an order or a contact is a field a
        person fills in.

        No `initialProjectId` default from "the user's most recent project":
        that needs a way to ask "which project did this user last file into"
        cheaply, and the only route there is fetching every task and scanning
        for the current user's newest one — sound for the ~35-task fixture
        (`TASK_FETCH_LIMIT`'s own comment), not for a live Postgres table this
        card would otherwise query on every order and contact page load. Left
        empty and required, the fallback #404 itself allows.
      */}
      <TaskForm
        // Keyed by the record: this card can be handed a different order
        // without remounting, and a form that kept the last one's title would
        // open "Follow up order #1001" on #1002.
        key={`${targetType}:${targetId}`}
        open={creating}
        onOpenChange={setCreating}
        link={{ targetType, targetId }}
        initialTitle={followUpTitle}
        heading={followUpTitle ? "New follow-up task" : undefined}
        onCreated={() => {
          void tasksQuery.refetch();
          // A task linked to an order changes that order's "Has open
          // follow-up", which the orders list may have cached (#445).
          if (targetType === "order") {
            void queryClient.invalidateQueries({ queryKey: ["orders"] });
          }
        }}
      />
    </Card>
  );
}
